import React from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaInstagram, FaHeart, FaCamera, FaShare, FaDownload, FaUsers, FaStar } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

function HomePage() {
  const { t } = useTranslation();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
      {/* Logo and Title */}
            <div className="flex items-center gap-3">
        <img 
          src="/new-logo.png" 
          alt="Weddingram Logo" 
                className="h-10 w-auto hover:scale-105 transition-transform duration-200"
        />
              {/* Weddingram Title - Next to logo on desktop, center on mobile */}
              <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent hidden md:block">
                weddingram
              </h1>
      </div>
            
            {/* Weddingram Title - Center on mobile only */}
            <div className="absolute left-1/2 transform -translate-x-1/2 md:hidden">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                weddingram
              </h1>
            </div>
            
            {/* Navigation - Hidden on mobile */}
            <nav className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-gray-700 hover:text-pink-600 transition-colors font-medium"
              >
                {t('homepage.navigation.howItWorks')}
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className="text-gray-700 hover:text-pink-600 transition-colors font-medium"
              >
                {t('homepage.navigation.features')}
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')}
                className="text-gray-700 hover:text-pink-600 transition-colors font-medium"
              >
                {t('homepage.navigation.testimonials')}
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-gray-700 hover:text-pink-600 transition-colors font-medium"
              >
                {t('homepage.navigation.pricing')}
              </button>
            </nav>
      
      {/* Language Switcher */}
        <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-pink-100 text-pink-800 mb-4">
                <FaHeart className="mr-2" />
                {t('homepage.hero.badge')}
              </span>
      </div>
      
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 mb-4 leading-tight">
              {t('homepage.hero.title')}
        </h1>
            
            <p className="text-xl sm:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              {t('homepage.hero.subtitle')}
            </p>
            
            <div className="flex flex-col gap-4 justify-center items-center mb-12">
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="https://wa.me/905372243441"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white text-lg font-semibold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3 transform hover:scale-105"
                >
                  <FaWhatsapp size={24} />
                  {t('homepage.hero.freeTrialButton')}
                </a>
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="relative px-8 py-4 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white text-lg font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2 border-yellow-400 animate-pulse"
                >
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-red-800 text-xs font-black px-2 py-1 rounded-full animate-bounce">
                    İNDİRİM!
                  </span>
                  <span className="flex items-center gap-2">
                    🔥 {t('homepage.hero.pricingButton')}
                  </span>
                </button>
                                <a
                  href="https://instagram.com/weddingram.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-base font-medium rounded-xl transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2 transform hover:scale-105"
                >
                  <FaInstagram size={20} />
                  {t('homepage.hero.instagramButton')}
                </a>
                
                
                 
                 <button 
                   onClick={() => scrollToSection('how-it-works')}
                   className="px-6 py-2 bg-white border-2 border-gray-200 hover:border-pink-300 text-gray-800 text-base font-medium rounded-xl transition-all duration-300 hover:bg-pink-50"
                 >
                   {t('homepage.hero.howItWorksButton')}
                 </button>
              </div>            
            </div>


          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('homepage.howItWorksSection.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('homepage.howItWorksSection.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="text-center group p-6 rounded-2xl border border-pink-200 bg-pink-50/30 hover:bg-pink-50/50 transition-all duration-300">
              <div className="w-24 h-24 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <img 
                  src="/assets/phone-holding-hand.png" 
                  alt="Phone holding hand"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.howItWorksSection.step1.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.howItWorksSection.step1.description')}
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center group p-6 rounded-2xl border border-pink-200 bg-pink-50/30 hover:bg-pink-50/50 transition-all duration-300">
              <div className="w-24 h-24 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <img 
                  src="/assets/upload-arrow.png" 
                  alt="Upload arrow"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.howItWorksSection.step2.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.howItWorksSection.step2.description')}
              </p>
               <p className="text-gray-600 leading-relaxed">
                {t('homepage.howItWorksSection.step2.description2')}
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center group p-6 rounded-2xl border border-pink-200 bg-pink-50/30 hover:bg-pink-50/50 transition-all duration-300">
              <div className="w-24 h-24 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <img 
                  src="/assets/bride-groom-back.png" 
                  alt="Bride and groom back"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.howItWorksSection.step3.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.howItWorksSection.step3.description')}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.howItWorksSection.step3.description2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Example Cards Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('homepage.exampleCards.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('homepage.exampleCards.subtitle')}
            </p>
          </div>

          {/* Scrollable Cards Container */}
          <div className="relative overflow-visible">
            <div className="flex overflow-x-auto scrollbar-hide gap-6 py-4" style={{scrollBehavior: 'smooth'}}>
              {[
                { id: 1, title: "Aysegul Aytug", color: "from-pink-50 to-rose-50", accent: "border-pink-200", image: "/example-designs/aysegul-aytug.png" },
                { id: 2, title: "Capture The Love", color: "from-pink-50 to-rose-50", accent: "border-pink-200", image: "/example-designs/capture-the-love.png" },
                { id: 3, title: "Yellow Drink", color: "from-amber-50 to-yellow-50", accent: "border-amber-200", image: "/example-designs/yellow-drink.png" },
                { id: 4, title: "Pink Flowers", color: "from-pink-50 to-rose-50", accent: "border-pink-200", image: "/example-designs/pink-flowers.png" },
              ].map((card) => (
                <div key={card.id} className="flex-shrink-0">
                  <div className="rounded-3xl shadow-xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl overflow-hidden">
                    <img 
                      src={card.image} 
                      alt={card.title}
                      className="w-auto h-96 sm:h-[28rem] object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Scroll Indicators */}
            <div className="flex justify-center mt-6 gap-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="w-2 h-2 bg-gray-300 rounded-full hover:bg-pink-400 transition-colors cursor-pointer"></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Pages Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('homepage.customerPages.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('homepage.customerPages.subtitle')}
            </p>
          </div>

          {/* Scrollable Pages Container */}
          <div className="relative overflow-visible">
            <div className="flex overflow-x-auto scrollbar-hide gap-6 py-4" style={{scrollBehavior: 'smooth'}}>
              {[
                { id: 1, title: "Aleyna & Necdet", image: "/example-pages-bg/aleyna-necdet.jpg" },
                { id: 2, title: "Başak & Sarp", image: "/example-pages-bg/basak-sarp.jpg" },
                { id: 3, title: "Ecem & Ali", image: "/example-pages-bg/ecem-ali.jpg" },
                { id: 4, title: "Gül & Oğuz", image: "/example-pages-bg/gul-oguz.jpg" },
                { id: 5, title: "Melis & Anıl", image: "/example-pages-bg/melis-anil.jpg" },
                { id: 6, title: "Noemy & Daniel", image: "/example-pages-bg/noemy-daniel.jpg" },
                { id: 7, title: "Selen Ezgi & Onur", image: "/example-pages-bg/selenEzgi-onur.jpg" },
                { id: 8, title: "Serra & Selim", image: "/example-pages-bg/serra-selim.jpg" }
              ].map((page) => (
                <div key={page.id} className="flex-shrink-0">
                  <div className="rounded-3xl shadow-xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl overflow-hidden h-96 sm:h-[28rem] w-80 sm:w-96 relative group">
                    <img 
                      src={page.image} 
                      alt={page.title}
                      className="w-full h-full object-contain bg-gradient-to-br from-purple-50 to-pink-50"
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Scroll Indicators */}
            <div className="flex justify-center mt-6 gap-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="w-2 h-2 bg-gray-300 rounded-full hover:bg-purple-400 transition-colors cursor-pointer"></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('homepage.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              {t('homepage.pricing.subtitle')}
            </p>
            
            <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 border-2 border-amber-200">
              <div className="text-center mb-8">
                
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('homepage.pricing.packageTitle')}</h3>
                <div className="text-5xl sm:text-6xl font-bold text-amber-600 mb-2">{t('homepage.pricing.price')}</div>
                <p className="text-gray-600">{t('homepage.pricing.priceSubtitle')}</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.allEvents.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.allEvents.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.customDesign.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.customDesign.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.personalPage.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.personalPage.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.unlimitedUpload.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.unlimitedUpload.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.secureStorage.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.secureStorage.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.mobileCompatible.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.mobileCompatible.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.instantAccess.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.instantAccess.description')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('homepage.pricing.features.bulkDownload.title')}</h4>
                    <p className="text-gray-600 text-sm">{t('homepage.pricing.features.bulkDownload.description')}</p>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a
                  href="https://wa.me/905372243441"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white text-lg font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2 border-yellow-400 animate-pulse"
                >
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-red-800 text-xs font-black px-2 py-1 rounded-full animate-bounce">
                    İNDİRİM!
                  </span>
                  <FaWhatsapp size={24} />
                  <span className="flex items-center gap-1">
                    🔥 {t('homepage.pricing.orderButton')}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('homepage.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('homepage.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-pink-100 to-pink-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FaCamera className="text-2xl text-pink-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.features.easyUpload.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.features.easyUpload.description')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-purple-100 to-purple-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FaShare className="text-2xl text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.features.instantSharing.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.features.instantSharing.description')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FaDownload className="text-2xl text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.features.bulkDownload.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.features.bulkDownload.description')}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-green-100 to-green-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FaUsers className="text-2xl text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.features.unlimitedGuests.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.features.unlimitedGuests.description')}
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FaHeart className="text-2xl text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.features.secureStorage.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.features.secureStorage.description')}
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FaStar className="text-2xl text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('homepage.features.customDesign.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('homepage.features.customDesign.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('homepage.testimonials.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('homepage.testimonials.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

            {/* Testimonial 4 */}
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl p-8 shadow-lg border border-amber-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  G
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Gül & Oğuz</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
                             <p className="text-gray-600 italic leading-relaxed">
                 "Düğünümüze 18 saat kala bir umut kendilerine ulaştık, her şeyi hazır hale getirdiler! Bununla yetmedi düğünümüze özel Azerbaycanca dil desteği bile eklediler. 
                 Oğuz'un ailesi çok memnun kaldı. Hele o notları okurken çok duygulandık. Gerçekten harika bir ekip! Tekrar tekrar teşekkür ediyoruz!"
               </p>
            </div>

            {/* Testimonial 1 */}
            <div className="bg-gradient-to-br from-pink-50 to-white rounded-3xl p-8 shadow-lg border border-pink-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  A
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Aleyna & Necdet</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "Weddingram sayesinde düğünümüzde çekilen tüm fotoğrafları topladık! 
                Ekip çok ilgiliydi, düğünümüze günler kala ihtiyaçlarımızı sordular. 
                Misafirlerimizin gözünden çok özel anlar yakaladık. 
                Her fotoğrafın altına yazdığımız notları okurken tekrar tekrar ağladık. 
                O anları yaşarken hissettiğimiz duyguları hatırladık. Kesinlikle tavsiye ederiz!"
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-3xl p-8 shadow-lg border border-purple-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  B
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Başak & Sarp</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "QR kod kartları çok şıktı! Misafirlerimiz çok beğendi ve kolayca fotoğraf yüklediler.
                Teknoloji bilmeyen akrabalarımız bile rahatlıkla kullandı. 
                Ekip bize özel video rehber bile hazırlamıştı. Ne kadar düşünceli!
                Fotoğrafların yanına yazdığımız küçük notları okuyunca çok duygulandık. 
                "
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-gradient-to-br from-rose-50 to-white rounded-3xl p-8 shadow-lg border border-rose-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-rose-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  E
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Ecem & Ali</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "Düğün sonrası 500'den fazla fotoğraf ve video topladık! 
                Weddingram ekibi sürekli yanımızdaydı, her sorumuzda hemen geri döndüler. 
                Profesyonel fotoğrafçının bile kaçırdığı anları yakaladık. Harika bir deneyimdi! 📸"
              </p>
            </div>

            

            {/* Testimonial 5 */}
            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl p-8 shadow-lg border border-emerald-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  M
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Melis & Anıl</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "Özel tasarım sayfamız çok güzeldi! Misafirlerimiz not da ekleyebildi. 
                Ekip gerçekten çok samimi ve yardımsever, düğün stresimizi hiç artırmadılar. 
                Düğün sonrası okumak çok duygusal oldu. Bu hizmeti herkese öneriyoruz! 💝"
              </p>
            </div>

           

            {/* Testimonial 7 */}
            <div className="bg-gradient-to-br from-indigo-50 to-white rounded-3xl p-8 shadow-lg border border-indigo-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  S
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Selen Ezgi & Onur</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "Toplu indirme özelliği harika! Tüm fotoğrafları tek tıkla aldık. 
                Düğün günü bile ekip bize her şeyin yolunda olup olmadığını sordu. 
                Arkadaşlarımızla da kolayca paylaştık. Çok pratik bir sistem!"
              </p>
            </div>

            {/* Testimonial 8 */}
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-3xl p-8 shadow-lg border border-teal-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  S
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Serra & Selim</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "Sınırsız yükleme özelliği çok iyiydi! Misafirlerimiz istediği kadar fotoğraf yükledi. 
                Hiç sınırlama olmadı. Bir misafirimiz tek başına 200 fotoğraf yükledi, hiç sorun çıkmadı! 
                Ekip düğün sonrası bile 'Nasıl geçti?' diye sordu. 
                Fotoğrafları incelerken yazdığımız notları görünce çok duygulandık. 
                Her kareden bir hikaye çıkıyordu. Gerçekten çok teşekkür ediyoruz!"
              </p>
            </div>

            {/* Testimonial 9 */}
            <div className="bg-gradient-to-br from-pink-50 to-white rounded-3xl p-8 shadow-lg border border-pink-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  A
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Ayşegül & Aytuğ</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "Kişisel yükleme sayfamız çok özel tasarlanmıştı! Gerçekten özenli çalıştılar. 
                Misafirlerimiz çok beğendi ve sürekli övdüler. Çok şık bir hizmet!"
              </p>
            </div>

            {/* Testimonial 10 */}
            <div className="bg-gradient-to-br from-violet-50 to-white rounded-3xl p-8 shadow-lg border border-violet-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-violet-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  B
                </div>
                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Bengü & Arda</h4>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} size={16} />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic leading-relaxed">
                "After party'de bile kullandık! Gece boyunca eğlenceli anlar topladık. 
                Weddingram ekibi her aşamada destek oldu, hiçbir konuda yalnız bırakmadılar. 
                Tek paket ile tüm etkinliklerimizi kapsadı. Tekrar tekrar teşekkür ediyoruz!"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-pink-500 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('homepage.cta.title')}
          </h2>
          <p className="text-xl text-pink-100 mb-12 max-w-2xl mx-auto">
            {t('homepage.cta.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <a
          href="https://wa.me/905372243441"
          target="_blank"
          rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-pink-600 text-lg font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3 transform hover:scale-105"
        >
              <FaWhatsapp size={24} />
              {t('homepage.cta.startButton')}
        </a>

        <a
          href="https://instagram.com/weddingram.me"
          target="_blank"
          rel="noopener noreferrer"
              className="px-8 py-4 bg-transparent border-2 border-white text-white text-lg font-bold rounded-2xl transition-all duration-300 hover:bg-white hover:text-pink-600 flex items-center gap-3"
            >
              <FaInstagram size={24} />
              {t('homepage.cta.instagramButton')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <img 
                src="/new-logo.png" 
                alt="Weddingram Logo" 
                className="h-12 w-auto mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-400 leading-relaxed max-w-md">
                {t('homepage.footer.description')}
              </p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4">{t('homepage.footer.quickLinks')}</h3>
              <ul className="space-y-2">
                <li>
                  <button 
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t('homepage.navigation.howItWorks')}
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('pricing')}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t('homepage.navigation.pricing')}
                  </button>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                    {t('homepage.footer.contact')}
        </Link>
                </li>
              </ul>
      </div>
            
            {/* Contact */}
            <div>
              <h3 className="text-lg font-bold mb-4">{t('homepage.footer.contact')}</h3>
              <div className="space-y-3">
        <a
          href="mailto:weddingram.me@gmail.com"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  📧 weddingram.me@gmail.com
                </a>
                <a
                  href="https://wa.me/905372243441"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <FaWhatsapp /> WhatsApp
                </a>
                <a
                  href="https://instagram.com/weddingram.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                >
                  <FaInstagram /> Instagram
        </a>
      </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400">
              {t('homepage.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage; 
